import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import {
  DEFAULT_LOCALE,
  I18N_FALLBACK_LOCALE,
  I18N_NAMESPACE_KEYS,
  resources,
  SUPPORTED_LOCALES,
} from '@repo/common/i18n';

import { lazyLocaleBackend } from './locale-lazyload';

export type CreateAppI18nOptions = {
  debug?: boolean;
};

/**
 * Configure the shared i18next singleton for Vite apps (web, admin, partner).
 * English is bundled; other locales load via {@link lazyLocaleBackend}.
 */
export function createAppI18n(options: CreateAppI18nOptions = {}): typeof i18n {
  if (!i18n.isInitialized) {
    void i18n
      .use(lazyLocaleBackend)
      .use(initReactI18next)
      .init({
        resources,
        partialBundledLanguages: true,
        fallbackLng: I18N_FALLBACK_LOCALE,
        supportedLngs: [...SUPPORTED_LOCALES],
        lng: DEFAULT_LOCALE,
        // Without this, i18next requests the legacy default namespace `translation`
        // (not present in our JSON files), which hits the lazy backend for en/uk.
        ns: [...I18N_NAMESPACE_KEYS],
        defaultNS: 'common',
        fallbackNS: 'common',
        interpolation: {
          // React escapes interpolated values in JSX; i18next double-escaping would
          // produce visible &amp; artifacts. Safe unless translations are rendered via
          // dangerouslySetInnerHTML.
          escapeValue: false,
        },
        // With lazy-loaded locales, hooks render immediately (English / key fallback)
        // while async bundles load; Suspense would flash a loading boundary.
        react: { useSuspense: false },
        debug: options.debug ?? false,
      });
  }

  return i18n;
}
